import React, { useMemo, useState } from "react";
import FullScreenModal from "../FullScreenModal/FullScreenModal";
import { IReducer } from "../../state/reducer";
import InventorySettingsRow from "../InventorySettingsRow/InventorySettingsRow";
import { BrickInventoryEntry } from "../../state/BrickInventory/BrickInventory";
import "./InventorySettings.css";
import "../PrimaryFilter/PrimaryFilter.css";

interface InventorySettingsProps extends IReducer {
  isOpen: boolean;
  onClose: (arg0: boolean) => void;
}

const InventorySettings: React.FC<InventorySettingsProps> = ({
  state,
  dispatch,
  isOpen,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  // Get all discovered bricks (amount is not NaN), including those hidden by user
  const discoveredBricks = useMemo(() => {
    let bricks: BrickInventoryEntry[] = [];

    state.bricks.forEach((brickEntry) => {
      if (brickEntry && !isNaN(brickEntry.amount)) {
        bricks.push(brickEntry);
      }
    });

    return bricks;
  }, [state.bricks]);

  // Filter bricks based on search term
  const filteredBricks = useMemo(() => {
    if (!searchTerm.trim()) {
      return discoveredBricks;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return discoveredBricks.filter(
      (brick) =>
        brick.name.toLowerCase().includes(lowerSearchTerm) ||
        brick.fullName.toLowerCase().includes(lowerSearchTerm) ||
        brick.type.toLowerCase().includes(lowerSearchTerm) ||
        brick.id.toLowerCase().includes(lowerSearchTerm),
    );
  }, [discoveredBricks, searchTerm]);

  return (
    <FullScreenModal
      header={"Inventory Settings"}
      isOpen={isOpen}
      onClose={() => onClose(false)}
      helpURL="https://www.google.com"
    >
      <div className="inventorySettingsContent">
        <div className="inventorySettingsSection">
          <div className="inventorySettingsSectionTitle">
            Reset Inventory Order
          </div>
          <div className="inventorySettingsSectionDescription">
            Reset the custom inventory order to the default order. This cannot
            be undone.
          </div>
          <button
            className="resetOrderButton"
            onClick={() => {
              if (
                window.confirm(
                  "Are you sure you want to reset the inventory order to default? This cannot be undone.",
                )
              ) {
                dispatch({ type: "SET_INVENTORY_ORDER", inventoryOrder: [] });
              }
            }}
          >
            Reset Inventory Order
          </button>
        </div>

        <div className="inventorySettingsSection">
          <div className="inventorySettingsSectionTitle">
            Toggle Brick Visibility
          </div>
          <div className="inventorySettingsSectionDescription">
            Hide or show bricks in your Inventory. Hiding bricks is purely
            visual; they can still be collected, auto collected, acquired via
            brick packs, etc.
          </div>
          <div className="inventorySettingsSearchContainer">
            <input
              type="text"
              className="searchInput"
              placeholder="Search bricks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="inventorySettingsRows">
            {discoveredBricks.length > 0 ? (
              filteredBricks.length > 0 ? (
                filteredBricks.map((brick) => (
                  <InventorySettingsRow
                    key={brick.id}
                    id={brick.id}
                    name={brick.name}
                    type={brick.type}
                    userHidden={brick.userHidden}
                    state={state}
                    dispatch={dispatch}
                  />
                ))
              ) : (
                <div className="noDiscoveredBricksMessage">
                  No bricks match your search "{searchTerm}".
                </div>
              )
            ) : (
              <div className="noDiscoveredBricksMessage">
                No discovered bricks available yet. Keep playing to discover
                more!
              </div>
            )}
          </div>
        </div>
      </div>
    </FullScreenModal>
  );
};
export default InventorySettings;
